import React, { useRef } from 'react';
import _merge from 'lodash/merge';
import _isString from 'lodash/isString';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { getContext, useCombinedRefs } from 'common';
import useTooltip from 'common/useTooltip';
import Tooltip, { TooltipProps } from 'components/Tooltip/Tooltip';
import Styled from './styles';

const TITLE_PADDING = 24;
const {
  FONT_SIZE,
  FONT_FAMILY,
} = Tokens;
const SUBTITLE_FONT = `${FONT_SIZE.FONT_SIZE_12} ${FONT_FAMILY.NOKIA_PURE_TEXT}`;

export interface AppToolbarSubtitleProps extends React.ComponentPropsWithRef<'div'> {
  /** Subtitle of the AppToolbar */
  children: string | React.ReactNode;
  /** A tooltip for the subtitle, if it is a string,  is displayed when the  text does not
   * fit in the available title space. This property disables the subtitle tooltip. */
  disableTooltip?: boolean;
  /** Tooltip properties. See Tooltip component documentation for details. */
  tooltipProps?: Partial<TooltipProps>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** Sub-component of <AppToolbar /> that renders the toolbar subtitle.
 * Any other props will be passed to the root element.
*/
const AppToolbarSubtitle = React.forwardRef<HTMLElement, AppToolbarSubtitleProps>((props: AppToolbarSubtitleProps, ref) =>
  getContext(({ RTL, theme }: { theme?: any, RTL: boolean }) => {
    const {
      children,
      theme: propTheme,
      disableTooltip,
      tooltipProps,
      ...otherProps
    } = props;

    const subtitleRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, subtitleRef);
    const subtitle = _isString(children) ? children : undefined; // for Tooltip
    const showTooltip = useTooltip(combinedRef, subtitle, SUBTITLE_FONT, TITLE_PADDING);

    const resolvedTheme = theme && _merge({}, theme.TOOLBAR, propTheme);
    const subtitleContent = (
      <Styled.Subtitle
        $rtl={RTL}
        ref={combinedRef}
        $theme={resolvedTheme}
        {...otherProps}
      >
        {children}
      </Styled.Subtitle>
    );

    const tooltipPropsDisable = tooltipProps.disable !== undefined ? tooltipProps.disable : true;
    return (disableTooltip && tooltipPropsDisable) ?
      subtitleContent
      :
      (
        <Tooltip
          disable={!showTooltip || subtitle === undefined}
          placement="bottom"
          trigger={['hover', 'focus']}
          tooltip={subtitle || ''}
          modifiers={{ offset: { offset: '0, -120' } }}
          {...tooltipProps}
        >
          {subtitleContent}
        </Tooltip>
      );
  }));

AppToolbarSubtitle.displayName = 'AppToolbarSubtitle';
AppToolbarSubtitle.defaultProps = {
  disableTooltip: false,
  tooltipProps: {},
  theme: {}
};

export default AppToolbarSubtitle;
